import { useCallback, useEffect, useState } from 'react'
import { PROGRESS_STORAGE_KEY } from '../data/market/config'

/** Progreso de lectura local: solo claves de temas y de casillas. Nunca datos de cuenta o pagos. */
type Progress = { read: string[]; checks: string[] }

const EMPTY: Progress = { read: [], checks: [] }
const EVENT = 'vanguard-market-progress'

function load(): Progress {
  try {
    const raw = window.localStorage.getItem(PROGRESS_STORAGE_KEY)
    if (!raw) return EMPTY
    const p = JSON.parse(raw) as Partial<Progress>
    return { read: Array.isArray(p.read) ? p.read.filter((x) => typeof x === 'string') : [], checks: Array.isArray(p.checks) ? p.checks.filter((x) => typeof x === 'string') : [] }
  } catch {
    return EMPTY
  }
}

function save(p: Progress) {
  try {
    window.localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(p))
  } catch {
    /* almacenamiento no disponible: el progreso solo dura esta visita */
  }
  window.dispatchEvent(new Event(EVENT))
}

export function useMarketProgress() {
  const [progress, setProgress] = useState<Progress>(() => (typeof window === 'undefined' ? EMPTY : load()))

  useEffect(() => {
    const sync = () => setProgress(load())
    window.addEventListener(EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const toggle = (list: 'read' | 'checks', id: string) => {
    const cur = load()
    const has = cur[list].includes(id)
    save({ ...cur, [list]: has ? cur[list].filter((x) => x !== id) : [...cur[list], id] })
  }

  const toggleRead = useCallback((key: string) => toggle('read', key), [])
  const toggleCheck = useCallback((id: string) => toggle('checks', id), [])
  const reset = useCallback(() => save(EMPTY), [])

  return { read: progress.read, checks: progress.checks, isRead: (k: string) => progress.read.includes(k), isChecked: (id: string) => progress.checks.includes(id), toggleRead, toggleCheck, reset }
}
